import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adCampaignSettingsApi, type AdCampaignSettings } from '../../api/adCampaignSettingsApi';
import './AdCampaigns.css';

/**
 * Ad Campaigns — different jobs, kept apart:
 *
 *   1. Connect Meta         once per environment: dataset, token, test code, switch
 *   2. Connect Google Ads   once per environment: account, conversion actions, key, switch
 *   3. Tag the ads          the line to paste — per ad in Meta, once per account in Google
 *   4. Campaigns            what is running, as the clicks name it — discovered,
 *                           never registered here
 *
 * Settings live in the `ad-campaign-settings` doc, one per environment. The
 * secrets are write-only. See docs/FeatureDesigns/acquisition/meta-conversions-current.md
 * and google-conversions-current.md.
 */

/** Meta's own macros, filled per ad at click time: which platform (ig/fb),
 *  campaign, ad set and ad, plus the campaign id, which survives a rename. */
const META_URL_PARAMETERS =
  'utm_source={{site_source_name}}&utm_medium=paid&utm_campaign={{campaign.name}}'
  + '&utm_content={{ad.name}}&utm_term={{adset.name}}&utm_id={{campaign.id}}';

/** Google's ValueTrack parameters, filled at click time. Google has none for
 *  the campaign's name, so the id goes in both; name it under Site usage →
 *  Acquisition → Campaigns. The click id itself (gclid) is added by
 *  auto-tagging, not here. */
const GOOGLE_FINAL_URL_SUFFIX =
  'utm_source=google&utm_medium=paid&utm_campaign={campaignid}'
  + '&utm_content={creative}&utm_term={keyword}&utm_id={campaignid}';

const WINDOWS = [7, 30, 90] as const;

export function AdCampaigns() {
  return (
    <div className="adc">
      <h2 className="adc-title">Ad Campaigns</h2>
      <p className="adc-lead">Reports sign-ups and second-simulation opens from ads back to the platform the click came from — Meta or Google.</p>
      <ConnectMeta />
      <ConnectGoogle />
      <TagEachAd />
      <Campaigns />
    </div>
  );
}

function Step({ n, title, hint, aside, children }: {
  n: number; title: string; hint: string; aside?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="adc-step">
      <header className="adc-step-head">
        <span className="adc-num">{n}</span>
        <div>
          <h3>{title}</h3>
          <p className="adc-hint">{hint}</p>
        </div>
        {aside && <div className="adc-aside">{aside}</div>}
      </header>
      {children}
    </section>
  );
}

/* ── 1. Connect Meta ─────────────────────────────────────────────────── */

function ConnectMeta() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['adCampaignSettings'], queryFn: adCampaignSettingsApi.get });

  const [enabled, setEnabled] = useState(false);
  const [datasetId, setDatasetId] = useState('');
  const [testEventCode, setTestEventCode] = useState('');
  const [graphVersion, setGraphVersion] = useState('');
  /** undefined: keep the stored token. '': clear it. Otherwise: the new one. */
  const [newToken, setNewToken] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const reset = (d: AdCampaignSettings) => {
    setEnabled(d.meta.enabled);
    setDatasetId(d.meta.datasetId);
    setTestEventCode(d.meta.testEventCode);
    setGraphVersion(d.meta.graphVersion);
    setNewToken(undefined);
  };
  // eslint-disable-next-line react-hooks/set-state-in-effect -- load the form from the server once it arrives
  useEffect(() => { if (data) reset(data); }, [data]);

  const save = useMutation({
    mutationFn: () => adCampaignSettingsApi.update({
      meta: {
        enabled,
        datasetId: datasetId.trim(),
        testEventCode: testEventCode.trim(),
        graphVersion: graphVersion.trim(),
        ...(newToken !== undefined ? { accessToken: newToken.trim() } : {}),
      },
    }),
    onSuccess: (d) => {
      queryClient.setQueryData(['adCampaignSettings'], d);
      reset(d);
      setStatus({ ok: true, text: 'Saved' });
    },
    onError: (e) => setStatus({ ok: false, text: e instanceof Error ? e.message : 'Save failed' }),
  });

  const test = useMutation({
    mutationFn: adCampaignSettingsApi.sendTest,
    onSuccess: (r) => setStatus({ ok: r.ok, text: r.message }),
    onError: (e) => setStatus({ ok: false, text: e instanceof Error ? e.message : 'Test failed' }),
  });

  const badge = data && (data.meta.enabled && data.meta.datasetId && data.meta.hasAccessToken
    ? <span className="adc-badge is-on">{data.meta.testEventCode ? 'On · test mode' : 'On'}</span>
    : <span className="adc-badge">Off</span>);

  return (
    <Step n={1} title="Connect Meta" hint="Once per environment, shared by every campaign." aside={badge}>
      {isLoading && <p className="adc-muted">Loading…</p>}
      {error && <p className="adc-error">{(error as Error).message}</p>}
      {data && (() => {
        const m = data.meta;
        const dirty = enabled !== m.enabled || datasetId !== m.datasetId || testEventCode !== m.testEventCode
          || graphVersion !== m.graphVersion || newToken !== undefined;
        const tokenWillBeSet = newToken !== undefined ? newToken.trim() !== '' : m.hasAccessToken;
        return (
          <div className="adc-form">
            <label className="adc-field">
              <span className="adc-label">Dataset ID</span>
              <input className="adc-input" value={datasetId} onChange={e => setDatasetId(e.target.value)} placeholder="1234567890123456" />
              <span className="adc-help">Events Manager → your dataset → ID</span>
            </label>

            <div className="adc-field">
              <span className="adc-label">Access token</span>
              {newToken === undefined ? (
                <div className="adc-row">
                  <span className="adc-value">{m.hasAccessToken ? `Set · ends ${m.accessTokenLast4 ?? '????'}` : 'Not set'}</span>
                  <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewToken('')}>
                    {m.hasAccessToken ? 'Replace' : 'Add'}
                  </button>
                </div>
              ) : (
                <div className="adc-row">
                  <input className="adc-input" type="password" autoComplete="off" value={newToken}
                    onChange={e => setNewToken(e.target.value)} placeholder="Paste token (empty clears it)" />
                  <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewToken(undefined)}>Cancel</button>
                </div>
              )}
              <span className="adc-help">Dataset → Settings → Conversions API → Generate token</span>
            </div>

            <label className="adc-field">
              <span className="adc-label">Test event code</span>
              <input className="adc-input" value={testEventCode} onChange={e => setTestEventCode(e.target.value)} placeholder="TEST12345" />
              <span className="adc-help">Dev only — leave empty on prod.</span>
            </label>

            <label className="adc-check">
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              <span>Report to Meta</span>
            </label>

            <details className="adc-advanced">
              <summary>Advanced</summary>
              <label className="adc-field">
                <span className="adc-label">Graph API version</span>
                <input className="adc-input" value={graphVersion} onChange={e => setGraphVersion(e.target.value)} placeholder={data.defaultGraphVersion} />
              </label>
            </details>

            <div className="adc-actions">
              <button type="button" className="adc-btn adc-btn-primary"
                onClick={() => { setStatus(null); save.mutate(); }}
                disabled={!dirty || save.isPending || (enabled && (!datasetId.trim() || !tokenWillBeSet))}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="adc-btn adc-btn-ghost"
                onClick={() => { setStatus(null); test.mutate(); }}
                disabled={dirty || test.isPending || !m.datasetId || !m.hasAccessToken || !m.testEventCode}
                title={m.testEventCode ? 'Sends one event to Meta Test Events' : 'Save a test event code first'}>
                {test.isPending ? 'Sending…' : 'Send test event'}
              </button>
              {status && <span className={status.ok ? 'adc-ok' : 'adc-error'}>{status.text}</span>}
            </div>
          </div>
        );
      })()}
    </Step>
  );
}

/* ── 2. Connect Google Ads ───────────────────────────────────────────── */

const EMPTY_ACTIONS = { SecondSimulation: '', CompleteRegistration: '' };

function ConnectGoogle() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['adCampaignSettings'], queryFn: adCampaignSettingsApi.get });

  const [enabled, setEnabled] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [loginCustomerId, setLoginCustomerId] = useState('');
  const [actions, setActions] = useState(EMPTY_ACTIONS);
  const [validateOnly, setValidateOnly] = useState(false);
  /** undefined: keep the stored key. '': clear it. Otherwise: the new one. */
  const [newKey, setNewKey] = useState<string | undefined>(undefined);
  /** Same three states, for the developer token. */
  const [newDevToken, setNewDevToken] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const reset = (d: AdCampaignSettings) => {
    setNewDevToken(undefined);
    setEnabled(d.google.enabled);
    setCustomerId(d.google.customerId);
    setLoginCustomerId(d.google.loginCustomerId);
    setActions(d.google.conversionActions);
    setValidateOnly(d.google.validateOnly);
    setNewKey(undefined);
  };
  // eslint-disable-next-line react-hooks/set-state-in-effect -- load the form from the server once it arrives
  useEffect(() => { if (data) reset(data); }, [data]);

  const save = useMutation({
    mutationFn: () => adCampaignSettingsApi.update({
      google: {
        enabled,
        customerId: customerId.trim(),
        loginCustomerId: loginCustomerId.trim(),
        conversionActions: { SecondSimulation: actions.SecondSimulation.trim(), CompleteRegistration: actions.CompleteRegistration.trim() },
        validateOnly,
        ...(newKey !== undefined ? { serviceAccountKey: newKey.trim() } : {}),
        ...(newDevToken !== undefined ? { developerToken: newDevToken.trim() } : {}),
      },
    }),
    onSuccess: (d) => {
      queryClient.setQueryData(['adCampaignSettings'], d);
      reset(d);
      setStatus({ ok: true, text: 'Saved' });
    },
    onError: (e) => setStatus({ ok: false, text: e instanceof Error ? e.message : 'Save failed' }),
  });

  const test = useMutation({
    mutationFn: adCampaignSettingsApi.sendGoogleTest,
    onSuccess: (r) => setStatus({ ok: r.ok, text: r.message }),
    onError: (e) => setStatus({ ok: false, text: e instanceof Error ? e.message : 'Test failed' }),
  });

  if (!data) return null;
  const g = data.google;
  const badge = g.enabled && g.customerId && g.hasServiceAccountKey
    ? <span className="adc-badge is-on">{g.validateOnly ? 'On · validate only' : 'On'}</span>
    : <span className="adc-badge">Off</span>;
  const dirty = enabled !== g.enabled || customerId !== g.customerId || loginCustomerId !== g.loginCustomerId
    || actions.SecondSimulation !== g.conversionActions.SecondSimulation
    || actions.CompleteRegistration !== g.conversionActions.CompleteRegistration
    || validateOnly !== g.validateOnly || newKey !== undefined || newDevToken !== undefined;
  const keyWillBeSet = newKey !== undefined ? newKey.trim() !== '' : g.hasServiceAccountKey;
  const anyAction = !!(actions.SecondSimulation.trim() || actions.CompleteRegistration.trim());

  return (
    <Step n={2} title="Connect Google Ads" hint="Once per environment. Sent through Google's Data Manager API." aside={badge}>
      <div className="adc-form">
        <label className="adc-field">
          <span className="adc-label">Customer ID</span>
          <input className="adc-input" value={customerId} onChange={e => setCustomerId(e.target.value)} placeholder="123-456-7890" />
          <span className="adc-help">Top right of Google Ads, next to the account name</span>
        </label>

        <label className="adc-field">
          <span className="adc-label">Conversion action ID — Second simulation</span>
          <input className="adc-input" value={actions.SecondSimulation}
            onChange={e => setActions(a => ({ ...a, SecondSimulation: e.target.value }))} placeholder="987654321" />
        </label>
        <label className="adc-field">
          <span className="adc-label">Conversion action ID — Signed up</span>
          <input className="adc-input" value={actions.CompleteRegistration}
            onChange={e => setActions(a => ({ ...a, CompleteRegistration: e.target.value }))} placeholder="987654322" />
          <span className="adc-help">Goals → Conversions → New → Import → Other data sources or CRMs → Track conversions from clicks. Open the action: the number after <code>ctId=</code> in the URL.</span>
        </label>

        <div className="adc-field">
          <span className="adc-label">Service account key</span>
          {newKey === undefined ? (
            <div className="adc-row">
              <span className="adc-value">{g.hasServiceAccountKey ? `Set · ${g.serviceAccountEmail ?? 'unreadable'}` : 'Not set'}</span>
              <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewKey('')}>
                {g.hasServiceAccountKey ? 'Replace' : 'Add'}
              </button>
            </div>
          ) : (
            <div className="adc-row">
              <textarea className="adc-input" rows={4} autoComplete="off" spellCheck={false} value={newKey}
                onChange={e => setNewKey(e.target.value)} placeholder='Paste the JSON key ({"type": "service_account", …}); empty clears it' />
              <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewKey(undefined)}>Cancel</button>
            </div>
          )}
          <span className="adc-help">Google Cloud → enable the Data Manager API → IAM → Service accounts → Keys → Add key (JSON). Then add that account's email as a user in Google Ads (Admin → Access and security).</span>
        </div>

        <label className="adc-check">
          <input type="checkbox" checked={validateOnly} onChange={e => setValidateOnly(e.target.checked)} />
          <span>Validate only — Google checks every send and records nothing (dev)</span>
        </label>
        <label className="adc-check">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          <span>Report to Google</span>
        </label>

        <details className="adc-advanced">
          <summary>Advanced</summary>
          <label className="adc-field">
            <span className="adc-label">Manager account ID</span>
            <input className="adc-input" value={loginCustomerId} onChange={e => setLoginCustomerId(e.target.value)} placeholder="Only if the service account was added to a manager account" />
          </label>
          <div className="adc-field">
            <span className="adc-label">Developer token</span>
            {newDevToken === undefined ? (
              <div className="adc-row">
                <span className="adc-value">{g.hasDeveloperToken ? `Set · ends ${g.developerTokenLast4 ?? '????'}` : 'Not set'}</span>
                <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewDevToken('')}>
                  {g.hasDeveloperToken ? 'Replace' : 'Add'}
                </button>
              </div>
            ) : (
              <div className="adc-row">
                <input className="adc-input" type="password" autoComplete="off" value={newDevToken}
                  onChange={e => setNewDevToken(e.target.value)} placeholder="Paste token (empty clears it)" />
                <button type="button" className="adc-btn adc-btn-ghost" onClick={() => setNewDevToken(undefined)}>Cancel</button>
              </div>
            )}
            <span className="adc-help">Optional — Google ignores it since Sept 2026 (API access follows the Cloud project). Only the campaign sync sends it.</span>
          </div>
        </details>

        <div className="adc-actions">
          <button type="button" className="adc-btn adc-btn-primary"
            onClick={() => { setStatus(null); save.mutate(); }}
            disabled={!dirty || save.isPending || (enabled && (!customerId.trim() || !keyWillBeSet || !anyAction))}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="adc-btn adc-btn-ghost"
            onClick={() => { setStatus(null); test.mutate(); }}
            disabled={dirty || test.isPending || !g.customerId || !g.hasServiceAccountKey}
            title="Google validates one event per action — nothing is recorded">
            {test.isPending ? 'Checking…' : 'Check with Google'}
          </button>
          {status && <span className={status.ok ? 'adc-ok' : 'adc-error'}>{status.text}</span>}
        </div>
      </div>
    </Step>
  );
}

/* ── 3. Tag the ads ──────────────────────────────────────────────────── */

function Snippet({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard refused — the text is selectable */ }
  };
  return (
    <div className="adc-snippet">
      <code>{text}</code>
      <button type="button" className="adc-btn adc-btn-ghost" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

function TagEachAd() {
  return (
    <Step n={3} title="Tag the ads" hint="So each campaign and ad shows up as its own row below.">
      <p className="adc-text"><strong>Meta</strong>, for every new ad — Ad → <strong>Tracking</strong> → <strong>URL parameters</strong> → paste:</p>
      <Snippet text={META_URL_PARAMETERS} />
      <p className="adc-text"><strong>Google</strong>, once for the account — Admin → <strong>Account settings</strong> → <strong>Tracking</strong>:
        check <strong>Auto-tagging</strong> is on, then paste into <strong>Final URL suffix</strong>:</p>
      <Snippet text={GOOGLE_FINAL_URL_SUFFIX} />
    </Step>
  );
}

/* ── 3. Campaigns ────────────────────────────────────────────────────── */

function Campaigns() {
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ['adCampaigns', days],
    queryFn: () => adCampaignSettingsApi.campaigns(days),
  });

  const windowPicker = (
    <div className="adc-seg">
      {WINDOWS.map(d => (
        <button key={d} type="button" className={d === days ? 'is-active' : ''} onClick={() => setDays(d)}>{d}d</button>
      ))}
    </div>
  );

  return (
    <Step n={4} title="Campaigns" hint="Appear on their first click — nothing to add." aside={windowPicker}>
      {isLoading && <p className="adc-muted">Loading…</p>}
      {error && <p className="adc-error">{(error as Error).message}</p>}
      {data && (data.campaigns.length === 0
        ? <p className="adc-muted">No ad clicks in the last {data.days} days.</p>
        : (
          <div className="adc-table-wrap">
            <table className="adc-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Ad</th>
                  <th className="num">Clicks</th>
                  <th className="num">2+ sims</th>
                  <th className="num">Sign-ups</th>
                  <th>Last click</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map(c => (
                  <tr key={`${c.campaign ?? ''}|${c.content ?? ''}`} className={c.campaign ? '' : 'is-untagged'}>
                    <td>{c.campaign ?? 'Untagged (see step 3)'}</td>
                    <td>{c.content ?? '—'}</td>
                    <td className="num">{c.clicks.toLocaleString()}</td>
                    <td className="num">{c.secondSimulation.toLocaleString()}</td>
                    <td className="num">{c.signedUp.toLocaleString()}</td>
                    <td>{c.lastClickAt ? new Date(c.lastClickAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </Step>
  );
}
